import PropTypes from 'prop-types';
import { decimalFormatter } from '../../components/Table/utility';
import MoonIcon from '../../components/Icons/MoonIcon';
import cn from 'classnames';
import Tooltip from '@mui/material/Tooltip';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import { useState, memo, useEffect } from 'react';
import {
  deleteWorklogById,
  updateWorklogDay,
  createWorklogDay,
  getWorklogById,
} from '../../utils/API/worklog';
import TableCell from '@mui/material/TableCell';

const arePropsEqual = (prevProps, nextProps) =>
  JSON.stringify(nextProps) === JSON.stringify(prevProps);

export const WorklogDayCell = memo(
  ({
    worklog,
    date,
    issueId,
    isReadOnly,
    isWeekEnd,
    isHoliday,
    isToday,
    onDeleteClick,
    onEditClick,
    onDeleteCell,
    onUpdateCell,
  }) => {
    const [localWorklog, setLocalWorklog] = useState(worklog);

    useEffect(() => {
      setLocalWorklog(worklog);
    }, [worklog]);

    const [formattedTimeSpent, setFormattedTimeSpent] = useState(
      localWorklog ? decimalFormatter.format(localWorklog.time_spent) : ''
    );

    useEffect(() => {
      setFormattedTimeSpent(
        localWorklog ? decimalFormatter.format(localWorklog.time_spent) : ''
      );
    }, [localWorklog]);

    const handleDeleteWorklogById = async (worklogId) => {
      try {
        await deleteWorklogById({ id: worklogId });
        onDeleteCell(worklogId);
      } catch (error) {
        console.error('Failed to delete worklog:', error);
      }
    };

    const handleUpdateWorklogDay = async (data) => {
      try {
        await updateWorklogDay(data);
        const res = await getWorklogById(data.id);
        onUpdateCell(res);
      } catch (error) {
        console.error('Failed to update worklog:', error);
      }
    };

    const handleCreateWorklogDay = async (data) => {
      try {
        const resArr = await createWorklogDay(data);
        const res = await getWorklogById(resArr[0].id);
        onUpdateCell(res);
      } catch (error) {
        console.error('Failed to create worklog:', error);
      }
    };

    const cnClasses = cn({
      weekend: isWeekEnd && !isHoliday,
      holiday: isHoliday,
      today: isToday,
    });

    const handleTimeSpentChange = async () => {
      const raw = formattedTimeSpent.replace(',', '.');
      const parsed = parseFloat(raw);

      // Treat 0 and invalid input as NaN
      const value = parsed === 0 || isNaN(parsed) ? NaN : parsed;

      if (Number.isNaN(value)) {
        // Clear the input field visually
        setFormattedTimeSpent('');

        // If worklog exists and value is invalid, delete it
        if (localWorklog) {
          await handleDeleteWorklogById(localWorklog.id);
        }
        // If worklog doesn't exist and value is invalid, do nothing
        return;
      }

      if (localWorklog) {
        // If value is unchanged, do nothing
        if (value === localWorklog.time_spent) return;

        // If value changed, update
        await handleUpdateWorklogDay({
          id: localWorklog.id,
          time_spent: value,
          date,
          issue: localWorklog.issue,
        });
      } else {
        // Create new worklog
        await handleCreateWorklogDay({
          issue: issueId,
          date,
          time_spent: value,
          description: '',
        });
      }
    };

    const handleInputChange = (event) => {
      const input = event.target.value;

      // Allow empty string or valid decimal numbers (including 0)
      if (input === '' || /^[0-9]*([.,])?[0-9]*$/.test(input)) {
        setFormattedTimeSpent(input);
      }
    };

    return (
      <TableCell className={cnClasses}>
        <Tooltip
          placement="top"
          arrow
          title={
            localWorklog &&
            !isReadOnly && (
              <>
                <IconButton onClick={() => onDeleteClick(localWorklog.id)}>
                  <DeleteOutlinedIcon />
                </IconButton>
                <IconButton onClick={() => onEditClick(localWorklog)}>
                  <EditOutlinedIcon />
                </IconButton>
              </>
            )
          }
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              gap: '4px',
            }}
          >
            <TextField
              variant="standard"
              disabled={isReadOnly || localWorklog?.night_time_spent > 0}
              InputProps={{
                disableUnderline: true,
                inputMode: 'decimal',
                style: { width: '40px', marginLeft: localWorklog?.night_time_spent > 0 ? '-4px' : '0' },
              }}
              value={formattedTimeSpent}
              onChange={handleInputChange}
              onBlur={handleTimeSpentChange}
              onKeyDown={(e) => e.key === 'Enter' && handleTimeSpentChange()}
            />
            {localWorklog?.night_time_spent > 0 && (
              <span
                style={{
                  paddingRight: 2,
                  paddingBottom: 2,
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                <MoonIcon size={12} />
              </span>
            )}
          </div>
        </Tooltip>
      </TableCell>
    );
  },
  arePropsEqual
);

WorklogDayCell.propTypes = {
  worklog: PropTypes.shape({
    id: PropTypes.number,
    time_spent: PropTypes.number,
    issue: PropTypes.number,
  }),
  date: PropTypes.string,
  issueId: PropTypes.number,
  isReadOnly: PropTypes.bool,
  isWeekEnd: PropTypes.bool,
  isHoliday: PropTypes.bool,
  isToday: PropTypes.bool,
  onDeleteClick: PropTypes.func,
  onEditClick: PropTypes.func,
  onUpdateCell: PropTypes.func,
  onDeleteCell: PropTypes.func,
};

WorklogDayCell.defaultProps = {
  worklog: undefined,
  date: undefined,
  issueId: undefined,
  isReadOnly: false,
  isWeekEnd: false,
  isHoliday: false,
  isToday: false,
  onDeleteClick: () => {},
  onEditClick: () => {},
  onUpdateCell: () => {},
  onDeleteCell: () => {},
};

WorklogDayCell.displayName = 'WorklogDayCell';
